/**
 * Offline Storage Utility
 * Handles IndexedDB storage for offline data persistence
 */

const DB_NAME = 'DiaWiseOfflineDB';
const DB_VERSION = 1;

// Store names
const STORES = {
  GLUCOSE: 'glucose_pending',
  MEALS: 'meals_pending',
  MEDICATIONS: 'medications_pending',
  SYNC_QUEUE: 'sync_queue'
};

let db = null;

/**
 * Initialize the IndexedDB database
 */
export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB');
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Create stores for pending data
      Object.values(STORES).forEach(storeName => {
        if (!database.objectStoreNames.contains(storeName)) {
          database.createObjectStore(storeName, { keyPath: 'id', autoIncrement: true });
        }
      });
    };
  });
};

/**
 * Add data to offline storage
 */
export const addToOfflineStore = async (storeName, data) => {
  await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const dataWithTimestamp = {
      ...data,
      offlineTimestamp: new Date().toISOString(),
      synced: false
    };
    
    const request = store.add(dataWithTimestamp);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get all pending data from a store
 */
export const getPendingData = async (storeName) => {
  await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear synced data from a store
 */
export const clearSyncedData = async (storeName, ids) => {
  await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    ids.forEach(id => store.delete(id));
    
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Check if online
 */
export const isOnline = () => navigator.onLine;

/**
 * Register for background sync
 */
export const registerBackgroundSync = async (tag) => {
  if ('serviceWorker' in navigator && 'sync' in window.SyncManager) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register(tag);
      console.log(`Background sync registered: ${tag}`);
    } catch (error) {
      console.error('Background sync registration failed:', error);
    }
  }
};

/**
 * Save glucose reading offline
 */
export const saveGlucoseOffline = async (glucoseData) => {
  await addToOfflineStore(STORES.GLUCOSE, glucoseData);
  await registerBackgroundSync('sync-glucose');
  return { offline: true, message: 'Glucose reading saved offline. Will sync when online.' };
};

/**
 * Save meal offline
 */
export const saveMealOffline = async (mealData) => {
  await addToOfflineStore(STORES.MEALS, mealData);
  await registerBackgroundSync('sync-meals');
  return { offline: true, message: 'Meal saved offline. Will sync when online.' };
};

/**
 * Get pending glucose readings
 */
export const getPendingGlucose = () => getPendingData(STORES.GLUCOSE);

/**
 * Get pending meals
 */
export const getPendingMeals = () => getPendingData(STORES.MEALS);

/**
 * Sync all pending data
 */
export const syncAllPendingData = async (apiService) => {
  if (!isOnline()) {
    return { success: false, message: 'No internet connection' };
  }

  const results = {
    glucose: { synced: 0, failed: 0 },
    meals: { synced: 0, failed: 0 }
  };

  // Sync glucose data
  const pendingGlucose = await getPendingGlucose();
  const syncedGlucoseIds = [];
  
  for (const reading of pendingGlucose) {
    try {
      await apiService.glucose.create(reading);
      syncedGlucoseIds.push(reading.id);
      results.glucose.synced++;
    } catch (error) {
      results.glucose.failed++;
    }
  }
  
  if (syncedGlucoseIds.length > 0) {
    await clearSyncedData(STORES.GLUCOSE, syncedGlucoseIds);
  }

  // Sync meal data
  const pendingMeals = await getPendingMeals();
  const syncedMealIds = [];
  
  for (const meal of pendingMeals) {
    try {
      await apiService.meals.create(meal);
      syncedMealIds.push(meal.id);
      results.meals.synced++;
    } catch (error) {
      results.meals.failed++;
    }
  }
  
  if (syncedMealIds.length > 0) {
    await clearSyncedData(STORES.MEALS, syncedMealIds);
  }

  return {
    success: true,
    results
  };
};

/**
 * Get offline status summary
 */
export const getOfflineStatus = async () => {
  const pendingGlucose = await getPendingGlucose();
  const pendingMeals = await getPendingMeals();
  
  return {
    isOnline: isOnline(),
    pendingGlucose: pendingGlucose.length,
    pendingMeals: pendingMeals.length,
    totalPending: pendingGlucose.length + pendingMeals.length
  };
};

export default {
  initDB,
  isOnline,
  saveGlucoseOffline,
  saveMealOffline,
  syncAllPendingData,
  getOfflineStatus
};
